import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  TrendingUp,
  TrendingDown,
  Wallet,
  History,
  BarChart3,
  RefreshCw,
  Target,
  AlertTriangle,
  CheckCircle,
  XCircle,
  ArrowLeft,
  BookOpen,
  GraduationCap,
  HelpCircle,
} from "lucide-react";
import axios from "axios";
import { FlashCards } from "./FlashCards";
import { ChartQuiz } from "./ChartQuiz";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAIRS = [
  { symbol: "XAU/USD", name: "Gold" },
  { symbol: "EUR/USD", name: "EUR/USD" },
  { symbol: "GBP/USD", name: "GBP/USD" },
  { symbol: "BTC/USD", name: "Bitcoin" },
  { symbol: "ETH/USD", name: "Ethereum" },
];

export function PaperTrading({ token, onBack, user }) {
  const [account, setAccount] = useState(null);
  const [trades, setTrades] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("learn");  // Start with learning tab
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  
  // Trade form state
  const [tradeForm, setTradeForm] = useState({
    symbol: "XAU/USD",
    trade_type: "BUY",
    quantity: "0.1",
    stop_loss: "",
    take_profit: "",
  });
  
  const [currentPrice, setCurrentPrice] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchAccount = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/paper-trading/account`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      setAccount(res.data.account);
    } catch (err) {
      console.error(err);
    }
  }, [token]);

  const fetchTrades = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/paper-trading/trades`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      setTrades(res.data.trades || []);
    } catch (err) {
      console.error(err);
    }
  }, [token]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/paper-trading/stats`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      setStats(res.data.stats);
    } catch (err) {
      console.error(err);
    }
  }, [token]);

  const fetchPrice = useCallback(async (symbol) => {
    try {
      const urlSymbol = symbol.replace("/", "-").replace("!", "");
      const res = await axios.get(`${API}/price/${urlSymbol}`);
      setCurrentPrice(res.data.price);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    fetchAccount();
    fetchTrades();
    fetchStats();
    fetchPrice(tradeForm.symbol);
  }, [fetchAccount, fetchTrades, fetchStats, fetchPrice, tradeForm.symbol]);

  useEffect(() => {
    const interval = setInterval(() => {
      fetchPrice(tradeForm.symbol);
      if (activeTab === "positions") {
        fetchTrades();
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [tradeForm.symbol, activeTab, fetchPrice, fetchTrades]);

  const openTrade = async () => {
    setError("");
    setLoading(true);
    try {
      const payload = {
        symbol: tradeForm.symbol,
        trade_type: tradeForm.trade_type,
        quantity: parseFloat(tradeForm.quantity),
        stop_loss: tradeForm.stop_loss ? parseFloat(tradeForm.stop_loss) : null,
        take_profit: tradeForm.take_profit ? parseFloat(tradeForm.take_profit) : null,
      };
      await axios.post(`${API}/paper-trading/open`, payload, { headers });
      setSuccess("Trade opened successfully!");
      fetchAccount();
      fetchTrades();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to open trade");
    } finally {
      setLoading(false);
    }
  };

  const closeTrade = async (tradeId) => {
    setError("");
    try {
      const res = await axios.post(`${API}/paper-trading/close`, { trade_id: tradeId }, { headers });
      setSuccess(`Trade closed! P&L: $${res.data.pnl}`);
      fetchAccount();
      fetchTrades();
      fetchStats();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to close trade");
    }
  };

  const resetAccount = async () => {
    if (!window.confirm("Are you sure you want to reset your account? All trades will be closed.")) return;
    try {
      await axios.post(`${API}/paper-trading/reset`, {}, { headers });
      setSuccess("Account reset successfully!");
      fetchAccount();
      fetchTrades();
      fetchStats();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to reset account");
    }
  };

  const openTrades = trades.filter(t => t.status === "open");
  const closedTrades = trades.filter(t => t.status !== "open");

  return (
    <div className="min-h-screen bg-zinc-950 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={onBack} data-testid="paper-trading-back">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <BarChart3 className="w-8 h-8 text-blue-500" />
            <div>
              <h1 className="text-2xl font-bold text-white">Paper Trading</h1>
              <p className="text-sm text-zinc-400">Practice trading without real money</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-2">
              <p className="text-xs text-zinc-400">Virtual Balance</p>
              <p className="text-xl font-bold text-green-400 font-mono">
                ${account?.balance?.toFixed(2) || "10,000.00"}
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={resetAccount} className="border-red-500 text-red-400">
              Reset Account
            </Button>
          </div>
        </div>

        {/* Alerts */}
        {success && (
          <div className="bg-green-500/20 border border-green-500 text-green-400 p-3 rounded-lg mb-4 flex items-center gap-2">
            <CheckCircle className="w-5 h-5" />
            {success}
          </div>
        )}
        {error && (
          <div className="bg-red-500/20 border border-red-500 text-red-400 p-3 rounded-lg mb-4 flex items-center gap-2">
            <XCircle className="w-5 h-5" />
            {error}
          </div>
        )}

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          <Button
            variant={activeTab === "learn" ? "default" : "outline"}
            onClick={() => setActiveTab("learn")}
            className={activeTab === "learn" ? "bg-purple-600" : ""}
            size="sm"
          >
            <GraduationCap className="w-4 h-4 mr-1" /> Flash Cards
          </Button>
          <Button
            variant={activeTab === "quiz" ? "default" : "outline"}
            onClick={() => setActiveTab("quiz")}
            className={activeTab === "quiz" ? "bg-pink-600" : ""}
            size="sm"
          >
            <HelpCircle className="w-4 h-4 mr-1" /> Chart Quiz
          </Button>
          <Button
            variant={activeTab === "trade" ? "default" : "outline"}
            onClick={() => setActiveTab("trade")}
            className={activeTab === "trade" ? "bg-blue-600" : ""}
            size="sm"
          >
            <TrendingUp className="w-4 h-4 mr-1" /> Practice
          </Button>
          <Button
            variant={activeTab === "positions" ? "default" : "outline"}
            onClick={() => setActiveTab("positions")}
            className={activeTab === "positions" ? "bg-green-600" : ""}
            size="sm"
          >
            <Wallet className="w-4 h-4 mr-1" /> 
            Positions ({openTrades.length})
          </Button>
          <Button
            variant={activeTab === "history" ? "default" : "outline"}
            onClick={() => setActiveTab("history")}
            className={activeTab === "history" ? "bg-orange-600" : ""}
            size="sm"
          >
            <History className="w-4 h-4 mr-1" /> 
            History
          </Button>
          <Button
            variant={activeTab === "stats" ? "default" : "outline"}
            onClick={() => setActiveTab("stats")}
            className={activeTab === "stats" ? "bg-yellow-600" : ""}
            size="sm"
          >
            <BarChart3 className="w-4 h-4 mr-1" /> 
            Stats
          </Button>
        </div>

        {/* Learn Tab - Flash Cards */}
        {activeTab === "learn" && (
          <FlashCards />
        )}

        {/* Quiz Tab - Chart Quizzes */}
        {activeTab === "quiz" && (
          <ChartQuiz />
        )}

        {/* New Trade Tab */}
        {activeTab === "trade" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="bg-zinc-900 border-zinc-700">
              <CardHeader>
                <CardTitle className="text-white">Open New Trade</CardTitle>
                <CardDescription className="text-zinc-400">
                  Practice trading with virtual money
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Symbol Selection */}
                <div>
                  <label className="text-sm text-zinc-400 block mb-1">Symbol</label>
                  <Select 
                    value={tradeForm.symbol} 
                    onValueChange={(v) => { setTradeForm({...tradeForm, symbol: v}); fetchPrice(v); }}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      {PAIRS.map(p => (
                        <SelectItem key={p.symbol} value={p.symbol}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Current Price */}
                <div className="bg-zinc-800 p-4 rounded-lg text-center">
                  <p className="text-sm text-zinc-400">Current Price</p>
                  <p className="text-3xl font-bold font-mono text-white">
                    {currentPrice?.toFixed(5) || "Loading..."}
                  </p>
                </div>

                {/* Trade Type */}
                <div>
                  <label className="text-sm text-zinc-400 block mb-2">Trade Type</label>
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      onClick={() => setTradeForm({...tradeForm, trade_type: "BUY"})}
                      className={`h-16 text-lg ${tradeForm.trade_type === "BUY" ? "bg-green-600 hover:bg-green-700" : "bg-zinc-800 hover:bg-zinc-700"}`}
                    >
                      <TrendingUp className="w-5 h-5 mr-2" /> BUY
                    </Button>
                    <Button
                      onClick={() => setTradeForm({...tradeForm, trade_type: "SELL"})}
                      className={`h-16 text-lg ${tradeForm.trade_type === "SELL" ? "bg-red-600 hover:bg-red-700" : "bg-zinc-800 hover:bg-zinc-700"}`}
                    >
                      <TrendingDown className="w-5 h-5 mr-2" /> SELL
                    </Button>
                  </div>
                </div>

                {/* Quantity */}
                <div>
                  <label className="text-sm text-zinc-400 block mb-1">Quantity (Lots)</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={tradeForm.quantity}
                    onChange={(e) => setTradeForm({...tradeForm, quantity: e.target.value})}
                    className="bg-zinc-800 border-zinc-700"
                  />
                  <p className="text-xs text-zinc-500 mt-1">
                    Position Value: ${currentPrice ? (parseFloat(tradeForm.quantity) * currentPrice).toFixed(2) : "---"}
                  </p>
                </div>

                {/* Stop Loss & Take Profit */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-zinc-400 block mb-1">
                      <AlertTriangle className="w-3 h-3 inline mr-1 text-red-400" />
                      Stop Loss (Optional)
                    </label>
                    <Input
                      type="number"
                      step="0.00001"
                      value={tradeForm.stop_loss}
                      onChange={(e) => setTradeForm({...tradeForm, stop_loss: e.target.value})}
                      className="bg-zinc-800 border-zinc-700"
                      placeholder="e.g., 1900.00"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-zinc-400 block mb-1">
                      <Target className="w-3 h-3 inline mr-1 text-green-400" />
                      Take Profit (Optional)
                    </label>
                    <Input
                      type="number"
                      step="0.00001"
                      value={tradeForm.take_profit}
                      onChange={(e) => setTradeForm({...tradeForm, take_profit: e.target.value})}
                      className="bg-zinc-800 border-zinc-700"
                      placeholder="e.g., 2000.00"
                    />
                  </div>
                </div>

                <Button 
                  onClick={openTrade} 
                  className={`w-full h-12 text-lg ${tradeForm.trade_type === "BUY" ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"}`}
                  disabled={loading}
                >
                  {loading ? <RefreshCw className="w-5 h-5 animate-spin" /> : `Open ${tradeForm.trade_type} Trade`}
                </Button>
              </CardContent>
            </Card>

            {/* Quick Tips */}
            <Card className="bg-zinc-900 border-zinc-700">
              <CardHeader>
                <CardTitle className="text-white">Trading Tips</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm text-zinc-300">
                <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                  <p className="font-semibold text-blue-400 mb-1">Risk Management</p>
                  <p>Never risk more than 1-2% of your account on a single trade.</p>
                </div>
                <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <p className="font-semibold text-green-400 mb-1">Use Stop Loss</p>
                  <p>Always set a stop loss to protect your capital from large losses.</p>
                </div>
                <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                  <p className="font-semibold text-yellow-400 mb-1">Follow the Signal</p>
                  <p>Only take trades when confidence is 70%+ for better win rate.</p>
                </div>
                <div className="p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                  <p className="font-semibold text-purple-400 mb-1">Practice Makes Perfect</p>
                  <p>Use paper trading to test strategies before using real money.</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Open Positions Tab */}
        {activeTab === "positions" && (
          <Card className="bg-zinc-900 border-zinc-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Wallet className="w-5 h-5 text-green-400" />
                Open Positions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <div className="space-y-3">
                  {openTrades.map(trade => (
                    <div key={trade.id} className={`bg-zinc-800 p-4 rounded-lg border-l-4 ${trade.trade_type === "BUY" ? "border-l-green-500" : "border-l-red-500"}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-white">{trade.symbol}</span>
                            <Badge className={trade.trade_type === "BUY" ? "bg-green-600" : "bg-red-600"}>
                              {trade.trade_type}
                            </Badge>
                          </div>
                          <div className="grid grid-cols-3 gap-4 mt-2 text-sm">
                            <div>
                              <span className="text-zinc-400">Entry:</span>
                              <span className="font-mono text-white ml-1">{trade.entry_price?.toFixed(5)}</span>
                            </div>
                            <div>
                              <span className="text-zinc-400">Current:</span>
                              <span className="font-mono text-white ml-1">{trade.current_price?.toFixed(5) || "---"}</span>
                            </div>
                            <div>
                              <span className="text-zinc-400">Qty:</span>
                              <span className="font-mono text-white ml-1">{trade.quantity}</span>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`text-2xl font-bold ${(trade.unrealized_pnl || 0) >= 0 ? "text-green-400" : "text-red-400"}`}>
                            {(trade.unrealized_pnl || 0) >= 0 ? "+" : ""}{trade.unrealized_pnl?.toFixed(2) || "0.00"}
                          </p>
                          <p className={`text-sm ${(trade.unrealized_pnl_percentage || 0) >= 0 ? "text-green-400" : "text-red-400"}`}>
                            {(trade.unrealized_pnl_percentage || 0) >= 0 ? "+" : ""}{trade.unrealized_pnl_percentage?.toFixed(2) || "0.00"}%
                          </p>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={() => closeTrade(trade.id)}
                            className="mt-2 border-yellow-500 text-yellow-400 hover:bg-yellow-500/20"
                          >
                            Close Trade
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                  {openTrades.length === 0 && (
                    <p className="text-center text-zinc-500 py-8">No open positions. Open a trade to get started!</p>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        )}

        {/* Trade History Tab */}
        {activeTab === "history" && (
          <Card className="bg-zinc-900 border-zinc-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <History className="w-5 h-5 text-purple-400" />
                Trade History
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <div className="space-y-2">
                  {closedTrades.map(trade => (
                    <div key={trade.id} className="bg-zinc-800 p-3 rounded-lg flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Badge className={trade.trade_type === "BUY" ? "bg-green-600" : "bg-red-600"}>
                          {trade.trade_type}
                        </Badge>
                        <span className="font-semibold text-white">{trade.symbol}</span>
                        <span className="text-sm text-zinc-400">
                          {trade.entry_price?.toFixed(5)} → {trade.exit_price?.toFixed(5)}
                        </span>
                      </div>
                      <div className="text-right">
                        <p className={`font-bold ${(trade.pnl || 0) >= 0 ? "text-green-400" : "text-red-400"}`}>
                          {(trade.pnl || 0) >= 0 ? "+" : ""}${trade.pnl?.toFixed(2)}
                        </p>
                        <p className="text-xs text-zinc-500">
                          {new Date(trade.closed_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                  {closedTrades.length === 0 && (
                    <p className="text-center text-zinc-500 py-8">No closed trades yet.</p>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        )}

        {/* Statistics Tab */}
        {activeTab === "stats" && stats && (
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-zinc-900 border-zinc-700">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-white">{stats.total_trades}</p>
                  <p className="text-sm text-zinc-400">Total Trades</p>
                </CardContent>
              </Card>
              <Card className="bg-zinc-900 border-zinc-700">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-green-400">{stats.win_rate}%</p>
                  <p className="text-sm text-zinc-400">Win Rate</p>
                </CardContent>
              </Card>
              <Card className="bg-zinc-900 border-zinc-700">
                <CardContent className="p-4 text-center">
                  <p className={`text-3xl font-bold ${stats.total_pnl >= 0 ? "text-green-400" : "text-red-400"}`}>
                    ${stats.total_pnl?.toFixed(2)}
                  </p>
                  <p className="text-sm text-zinc-400">Total P&L</p>
                </CardContent>
              </Card>
              <Card className="bg-zinc-900 border-zinc-700">
                <CardContent className="p-4 text-center">
                  <p className={`text-3xl font-bold ${stats.total_pnl_percentage >= 0 ? "text-green-400" : "text-red-400"}`}>
                    {stats.total_pnl_percentage >= 0 ? "+" : ""}{stats.total_pnl_percentage?.toFixed(2)}%
                  </p>
                  <p className="text-sm text-zinc-400">Return</p>
                </CardContent>
              </Card>
            </div>

            {/* Detailed Stats */}
            <Card className="bg-zinc-900 border-zinc-700">
              <CardHeader>
                <CardTitle className="text-white">Performance Details</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                  <div>
                    <p className="text-sm text-zinc-400">Winning Trades</p>
                    <p className="text-2xl font-bold text-green-400">{stats.winning_trades}</p>
                  </div>
                  <div>
                    <p className="text-sm text-zinc-400">Losing Trades</p>
                    <p className="text-2xl font-bold text-red-400">{stats.losing_trades}</p>
                  </div>
                  <div>
                    <p className="text-sm text-zinc-400">Average Trade</p>
                    <p className={`text-2xl font-bold ${stats.average_trade >= 0 ? "text-green-400" : "text-red-400"}`}>
                      ${stats.average_trade?.toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-zinc-400">Best Trade</p>
                    <p className="text-2xl font-bold text-green-400">
                      ${stats.best_trade?.toFixed(2) || "---"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-zinc-400">Worst Trade</p>
                    <p className="text-2xl font-bold text-red-400">
                      ${stats.worst_trade?.toFixed(2) || "---"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-zinc-400">Account Balance</p>
                    <p className="text-2xl font-bold text-white">
                      ${account?.balance?.toFixed(2) || "10,000.00"}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
